import { graphql } from "gatsby"
import React from "react"
import {
  Layout,
  Seo,
  MykaV2Hero,
  MykaV2AskCards,
  MykaV2Support,
  MykaV2Partnerships,
  MykaV2Faq,
  MykaV2ContactCta,
} from "../../components"
import { stripTags } from "../../lib/utils"

import { absoluteUrl } from "../../lib/urls"

const SCHEMA_BASE = "https://findmymethod.org"

// Hardcoded, translated structural values per the Excel "Schema markup" sheet.
const SCHEMA_I18N = {
  en: {
    featureList: [
      "Contraceptive method comparison",
      "Pregnancy guidance",
      "Sexual health Q&A",
      "Anonymous and confidential",
      "24/7 availability",
      "Multilingual support",
    ],
    medicalAbout: ["Contraception", "Sexual Health", "Pregnancy"],
  },
  es: {
    featureList: [
      "Comparación de métodos anticonceptivos",
      "Orientación sobre el embarazo",
      "Salud sexual: preguntas y respuestas",
      "Anónimo y confidencial",
      "Disponibilidad 24/7",
      "Soporte multilingüe",
    ],
    medicalAbout: ["Anticoncepción", "Salud sexual", "Embarazo"],
  },
  fr: {
    featureList: [
      "Comparaison des méthodes contraceptives",
      "Conseils sur la grossesse",
      "Questions et réponses sur la santé sexuelle (Q&R)",
      "Anonyme et confidentiel",
      "Disponible 24h/24 et 7j/7",
      "Assistance en plusieurs langues",
    ],
    medicalAbout: ["Contraception", "Santé sexuelle", "Grossesse"],
  },
}

const ORG = {
  "@type": "Organization",
  name: "Women First Digital",
  email: "partnerships@womenfirstdigital.org",
}

// Fallback WhatsApp CTA label per language. Primary source is the ACF field
// `heroWhatsappUrlLabel`; this map only kicks in where that field is empty
// (currently es/fr).
const WHATSAPP_LABEL = {
  en: "Chat with Myka on WhatsApp",
  es: "Chatea con Myka en WhatsApp",
  fr: "Discutez avec Myka sur WhatsApp",
}

// Builds the SoftwareApplication / FAQPage / MedicalWebPage JSON-LD blocks.
// Dynamic fields (name, description, url, FAQ entities) come from WordPress so
// they never drift; structural fields are hardcoded per the content spec.
const buildJsonLd = ({ lang, slug, title, description, faqs }) => {
  const langKey = SCHEMA_I18N[lang] ? lang : "en"
  const i18n = SCHEMA_I18N[langKey]
  const langPrefix = langKey === "en" ? "" : `/${langKey}`
  const url = absoluteUrl(SCHEMA_BASE, `${langPrefix}${slug || ""}`)

  const blocks = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: title,
      description,
      applicationCategory: "HealthApplication",
      operatingSystem: "Web",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      inLanguage: ["en", "fr", "es"],
      url,
      provider: ORG,
      featureList: i18n.featureList,
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4",
        reviewCount: "5",
        bestRating: "5",
        worstRating: "1",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "MedicalWebPage",
      name: title,
      description,
      url,
      about: i18n.medicalAbout.map((name) => ({
        "@type": "MedicalCondition",
        name,
      })),
      audience: { "@type": "PeopleAudience", suggestedMinAge: 13 },
      inLanguage: ["en", "fr", "es"],
      publisher: ORG,
    },
  ]

  const faqEntities = faqs
    .filter((f) => f?.question)
    .map((f) => ({
      "@type": "Question",
      name: stripTags(f.question),
      acceptedAnswer: { "@type": "Answer", text: stripTags(f.answer) },
    }))

  if (faqEntities.length) {
    blocks.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqEntities,
    })
  }

  return blocks
}

const MykaV2Page = ({ data, pageContext }) => {
  const page = data?.PageDetails?.edges[0]?.node
  const lang = page?.fmmCore?.languageCode
  const slug = page?.fmmCore?.frontendSlug
  const copyrightMessage = stripTags(
    data?.FooterCopyright?.edges[0]?.node?.content
  )

  const hero = page?.hero || {}
  const mv2 = page?.mykaLandingV2 || {}

  const jsonLd = buildJsonLd({
    lang,
    slug,
    title: stripTags(hero.title),
    description: page?.fmmCore?.seoDescription,
    faqs: mv2.faqs || [],
  })

  return (
    <Layout
      lang={lang}
      languages={data?.Languages?.edges}
      copyrightMessage={copyrightMessage}
      slug={slug}
    >
      <Seo
        title={page?.fmmCore?.seoTitle}
        description={page?.fmmCore?.seoDescription}
        lang={lang}
        image={mv2.socialShareImage?.sourceUrl}
        imageAlt={mv2.socialShareImageAlt}
        jsonLd={jsonLd}
      />

      <MykaV2Hero
        title={hero.title}
        intro={hero.intro}
        linkLabel={hero.linkLabel}
        imageUrl={hero.image?.sourceUrl}
        imageAlt={mv2.heroImageAlt}
        availableLabel={mv2.heroAvailableLabel}
        whatsappUrl={mv2.heroWhatsappUrl}
        whatsappLabel={
          mv2.heroWhatsappUrlLabel || WHATSAPP_LABEL[lang] || WHATSAPP_LABEL.en
        }
        safetyNote={mv2.heroSafetyNote}
      />

      <MykaV2Support
        supportHeading={mv2.supportHeading}
        supportCards={mv2.supportCards || []}
      />

      <MykaV2AskCards
        askHeading={mv2.askHeading}
        askCards={mv2.askCards || []}
        ctaLabel={mv2.supportCtaLabel}
      />

      <MykaV2Faq
        faqHeading={mv2.faqHeading}
        faqs={mv2.faqs || []}
        faqCtaLabel={mv2.faqCtaLabel}
      />

      <MykaV2Partnerships
        partnerHeading={mv2.partnerHeading}
        partnerIntroBody={mv2.partnerIntroBody}
        partnerIntroIcon={mv2.partnerIntroIcon}
        partnerIntroIconAlt={mv2.partnerIntroIconAlt}
        partnerFeatures={mv2.partnerFeatures || []}
      />

      <MykaV2ContactCta
        heading={mv2.partnerClosingHeading}
        body={mv2.partnerClosingBody}
        ctaLabel={mv2.partnerCtaLabel}
        email={mv2.partnerContactEmail}
      />
    </Layout>
  )
}

export default MykaV2Page

export const query = graphql`
  query ($frontend_slug: String!, $language_code: String!) {
    PageDetails: allWpPage(
      filter: {
        fmmCore: {
          languageCode: { eq: $language_code }
          frontendSlug: { eq: $frontend_slug }
        }
      }
    ) {
      edges {
        node {
          id
          fmmCore {
            languageCode
            frontendSlug
            seoDescription
            seoTitle
          }
          hero {
            title
            intro
            linkLabel
            linkUrl
            image {
              sourceUrl
            }
          }
          mykaLandingV2 {
            heroAvailableLabel
            heroWhatsappUrl
            heroWhatsappUrlLabel
            heroSafetyNote
            heroImageAlt
            supportHeading
            supportCtaLabel
            supportCards {
              title
              body
              iconAlt
              icon { sourceUrl }
            }
            askHeading
            askCards {
              title
              body
              iconAlt
              icon { sourceUrl }
            }
            faqHeading
            faqCtaLabel
            faqs {
              question
              answer
            }
            partnerHeading
            partnerIntroBody
            partnerIntroIconAlt
            partnerIntroIcon { sourceUrl }
            partnerFeatures {
              title
              body
              iconAlt
              icon { sourceUrl }
            }
            partnerClosingHeading
            partnerClosingBody
            partnerCtaLabel
            partnerContactEmail
            socialShareImage {
              sourceUrl
            }
            socialShareImageAlt
          }
        }
      }
    }

    Languages: allWpPage(
      filter: { fmmCore: { frontendSlug: { eq: $frontend_slug } } }
    ) {
      edges {
        node {
          id
          fmmCore {
            languageCode
            frontendSlug
          }
        }
      }
    }

    FooterCopyright: allWpGenericBlock(
      filter: {
        fmmCore: { languageCode: { eq: $language_code } }
        generic_block_data: { type: { eq: "FooterCopyright" } }
      }
    ) {
      edges {
        node {
          title
          content
        }
      }
    }
  }
`
